import React, {useState, useEffect, useCallback} from 'react';
import {Route, useHistory, Switch, Redirect} from 'react-router-dom';
import {SecureRoute, Security} from '@okta/okta-react';
import {OktaAuth} from '@okta/okta-auth-js';
import produce, { setAutoFreeze } from 'immer';
import {ErrorBoundary, RequireRole, Sidebar, ErrorMessage} from 'components';
import {CustomLoginCallback, Home, Login, Config} from 'pages';
import {LoadState, UserRole} from 'enums';
import {SideNavigationWrapper, SideNavigationPageContent} from 'cfa-react-components';
import {axiosAPI, UserService, CaptureConfigService, ReplayConfigService} from 'services';

// We sometimes pass mutable objects down the component tree which shouldn't be frozen.
setAutoFreeze(false);

// Authentication via Okta
const oktaAuth = new OktaAuth({
    issuer: process.env.REACT_APP_OKTA_ISSUER,
    clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
    redirectUri: window.location.origin + '/login/callback',
});

export const oktaScopes = ['openid', 'email', 'profile'];

oktaAuth.authStateManager.subscribe((authState) => {
    const token = authState?.accessToken?.accessToken;

    if (token) {
        const headerVal = 'Bearer ' + token;
        axiosAPI.defaults.headers.common['Authorization'] = headerVal;
        UserService.parseOktaUserInfo(authState);
    }
});

interface IAppState {
    isAuthenticated: boolean,
    loadError?: string,
    loadPromise?: Promise<any>,
    loadStatus: LoadState,
}

export function App(props) {

    const [{
        isAuthenticated,
        loadError,
        loadPromise,
        loadStatus,
    }, setState] = useState<IAppState>({
        isAuthenticated: false,
        loadStatus: LoadState.Initial,
    });

    const history = useHistory();
    const onAuthRequired = () => {
        history.push('/login');
    }

    // We can't use the "useAuthState" hook here because we're outside the Security component.
    // So, to get the auth state, we'll manually subscribe.
    const onChangeAuthState = useCallback((authState) => {
        setState(produce(draft => {
            draft.isAuthenticated = authState?.isAuthenticated;
        }));
    }, []);

    useEffect(() => {
        oktaAuth.authStateManager.subscribe(onChangeAuthState);
        return () => {
            oktaAuth.authStateManager.unsubscribe(onChangeAuthState);
        }
    })

    // Load data commonly used across pages via services
    useEffect(() => {
        if (isAuthenticated && loadStatus === LoadState.Initial) {

            CaptureConfigService.getAll();
            ReplayConfigService.getAll();
            
            setState(produce(draft => {
                draft.loadStatus = LoadState.Success;
            }));
        }
    }, [isAuthenticated])

    return (
        <ErrorBoundary>
            <Security oktaAuth={oktaAuth} onAuthRequired={onAuthRequired}>
                <SideNavigationWrapper>
                    {isAuthenticated && (<Sidebar />)}
                    <SideNavigationPageContent>
                        <Switch>

                            <Route path='/login/callback' exact component={CustomLoginCallback} />
                            <Route path='/login' exact component={Login} />

                            {isAuthenticated && loadStatus === LoadState.Success && <>

                                {/* Need redirect here, since sidebar checks path to set active link */}
                                <SecureRoute path='/' exact component={() => (
                                    <RequireRole role={UserRole.ViewOnly}>
                                        <Redirect to={'/home'} />
                                    </RequireRole>
                                )} />

                                <SecureRoute path='/home' exact component={() => (
                                    <RequireRole role={UserRole.ViewOnly}>
                                        <Home />
                                    </RequireRole>
                                )} />

                                <SecureRoute path='/config' exact component={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <Config />
                                    </RequireRole>
                                )} />

                            </>}

                            {loadStatus === LoadState.Error && <ErrorMessage message={loadError} />}

                        </Switch>                            
                        <SecureRoute path='*' />
                    </SideNavigationPageContent>
                </SideNavigationWrapper>
            </Security>
        </ErrorBoundary>
    );
}